import { useQuery } from '@tanstack/react-query';
import { ArrowUp } from 'lucide-react';
import { projectsApi } from '../../../api/projects';
import { SectionHeader } from '../../../components/SectionHeader';
import { Project } from '../../../types/projects';

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * Reuses the existing WorkflowEngine/AuditEngine data - no separate
 * project history mechanism. Boxed stage view matching the OneGMS
 * reference, same CSS as WorkflowTrackingTab, but built directly from
 * workflow_state_history's own real from_state/to_state/comment columns
 * rather than through the generic audit-log action-name translation
 * layer other entities need - this data is already exactly the right
 * shape. actor_user_id has no joined name in this table, so the
 * tooltip shows "User #<id>" rather than inventing a name.
 */
export function ProjectApprovalsHistoryTab({ project }: { project: Project }) {
  const projectQuery = useQuery({ queryKey: ['project', project.id], queryFn: () => projectsApi.get(project.id) });
  const entries = projectQuery.data?.auditTrail ?? [];

  return (
    <div className="panel">
      <SectionHeader title="Approvals & History" />
      <div className="panel-body">
      <p className="muted">Current status: <strong>{project.status}</strong></p>

      {entries.length > 0 ? (
        <div className="workflow-tracking-scroll">
          {entries.map((entry, i) => {
            const tooltip = `User #${entry.actor_user_id}${entry.comment ? ` — ${entry.comment}` : ''}`;
            return (
              <div key={entry.id}>
                <div className="workflow-stage-row">
                  <div className="workflow-stage-box">{entry.from_state}</div>
                  <div className="workflow-stage-center">
                    <span className="workflow-stage-timestamp" title={tooltip}>{formatDateTime(entry.created_at)}</span>
                  </div>
                  <div className="workflow-stage-box">{entry.to_state}</div>
                </div>
                {i < entries.length - 1 && <div className="workflow-stage-arrow"><ArrowUp size={16} aria-hidden="true" /></div>}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="muted">No workflow history yet.</p>
      )}
      </div>
    </div>
  );
}

